import { parseColour, parseColourToVec4 } from "./colour";
import { Calibration, AnaglyphOrientation } from "./calibration";
import type { ColourCalibration } from "./calibration";
import {
  calibrationStillMatchesDevice,
  currentCalibrationDeviceIdentity,
} from "../../../app/shared/lib/calibration_core";

// Re-exports calibration types for use in other modules
export { AnaglyphOrientation, Calibration };
export type { ColourCalibration };

export interface CalibrationOverride {
  width_px?: number;
  height_px?: number;
  pixel_ratio?: number;
  height_cm?: number;
  distance_cm?: number;
  anaglyph_orientation?: AnaglyphOrientation;
  colour_correction?: ColourCalibration;
}

export interface AnaglyphPreferences {
  default_orientation?: AnaglyphOrientation;
  disallow_different_glasses?: boolean;
}

export enum Unit {
  Pixel = "px",
  Centimeter = "cm",
  PrismDiopter = "pdpt",
  Arcsecond = "arcsecond",
}

export interface Offset {
  value: number;
  unit: Unit;
}

export enum EyeSide {
  OD = "od", // Right eye
  OS = "os", // Left eye
}

export default class ExerciseScreen {
  calibration_is_set: boolean;
  calibration: Calibration;

  /**
   * CSS pixels per Phaser world pixel after FIT into the calibrated screen.
   * Defaults to 1 (world pixel == CSS pixel) until `setCanvasSize` is called.
   */
  private display_scale: number = 1;

  constructor(
    override: CalibrationOverride | null = null,
    anaglyph_preferences: AnaglyphPreferences | null = null,
  ) {
    const stored_calibration = ExerciseScreen.getCalibration();

    if (stored_calibration) {
      this.calibration_is_set = true;
      this.calibration = stored_calibration;
    } else {
      this.calibration_is_set = false;
      this.calibration = ExerciseScreen.guessCalibration(anaglyph_preferences);
    }

    // Override the calibration with any options passed in
    if (override) {
      if (override.width_px) {
        this.calibration.width_px = override.width_px;
      }
      if (override.height_px) {
        this.calibration.height_px = override.height_px;
      }
      if (override.pixel_ratio) {
        this.calibration.pixel_ratio = override.pixel_ratio;
      }
      if (override.height_cm) {
        this.calibration.height_cm = override.height_cm;
      }
      if (override.distance_cm) {
        this.calibration.distance_cm = override.distance_cm;
      }
      if (override.anaglyph_orientation) {
        this.calibration.anaglyph_orientation = override.anaglyph_orientation;
      }
      if (override.colour_correction) {
        this.calibration.colour_correction = override.colour_correction;
      }
    }

    // If the passed in calibration override was "complete", then mark calibration as set
    if (override && override.width_px && override.height_px && override.pixel_ratio && override.height_cm && override.distance_cm && override.anaglyph_orientation && override.colour_correction) {
      this.calibration_is_set = true;
    }

    this.calibration.anaglyph_orientation = ExerciseScreen.resolveAnaglyphOrientation(
      this.calibration.anaglyph_orientation,
      Boolean(stored_calibration),
      anaglyph_preferences,
      override?.anaglyph_orientation || ExerciseScreen.anaglyphOrientationOverrideFromQueryParams(),
    );
  }

  static anaglyphOrientationOverrideFromQueryParams(): AnaglyphOrientation | undefined {
    const orientation = new URLSearchParams(window.location.search).get(
      "calibration_override_anaglyph_orientation",
    );
    return Object.values(AnaglyphOrientation).includes(orientation as AnaglyphOrientation)
      ? orientation as AnaglyphOrientation
      : undefined;
  }

  static resolveAnaglyphOrientation(
    calibration_orientation: AnaglyphOrientation,
    calibration_is_set: boolean,
    anaglyph_preferences: AnaglyphPreferences | null = null,
    override_orientation?: AnaglyphOrientation,
  ): AnaglyphOrientation {
    const default_orientation = anaglyph_preferences?.default_orientation;
    if (
      default_orientation &&
      Object.values(AnaglyphOrientation).includes(default_orientation) &&
      (
        anaglyph_preferences?.disallow_different_glasses ||
        (!calibration_is_set && !override_orientation)
      )
    ) {
      return default_orientation;
    }
    return override_orientation || calibration_orientation;
  }

  // Get the device calibration stored in local storage.
  static getCalibration(): Calibration | null {
    let calibration = Calibration.load();
    if (calibration) {
      calibration = ExerciseScreen.override_from_query_params(calibration);
    }
    return calibration;
  }

  static guessCalibration(anaglyph_preferences: AnaglyphPreferences | null = null): Calibration {
    let calibration = new Calibration(
      anaglyph_preferences?.default_orientation,
    );
    calibration.width_px = ExerciseScreen.screen_width();
    calibration.height_px = ExerciseScreen.screen_height();
    calibration = ExerciseScreen.override_from_query_params(calibration);
    return calibration;
  }

  static override_from_query_params(calibration: Calibration) {
    // Override calibration from query params if specified.
    // Calibration can be overridden by passing in `calibration_override_<prop>`
    // For example '?calibration_override_distance_cm=500`
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("calibration_override_width_px")) {
      calibration.width_px = parseInt(urlParams.get("calibration_override_width_px")!);
    }
    if (urlParams.has("calibration_override_height_px")) {
      calibration.height_px = parseInt(urlParams.get("calibration_override_height_px")!);
    }
    if (urlParams.has("calibration_override_pixel_ratio")) {
      calibration.pixel_ratio = parseFloat(urlParams.get("calibration_override_pixel_ratio")!);
    }
    if (urlParams.has("calibration_override_height_cm")) {
      calibration.height_cm = parseFloat(urlParams.get("calibration_override_height_cm")!);
    }
    if (urlParams.has("calibration_override_distance_cm")) {
      calibration.distance_cm = parseFloat(urlParams.get("calibration_override_distance_cm")!);
    }
    if (urlParams.has("calibration_override_anaglyph_orientation")) {
      calibration.anaglyph_orientation = urlParams.get("calibration_override_anaglyph_orientation") as AnaglyphOrientation;
    }
    if (urlParams.has("calibration_override_red_brightness")) {
      calibration.colour_correction.red_brightness = parseFloat(urlParams.get("calibration_override_red_brightness")!);
    }
    if (urlParams.has("calibration_override_blue_brightness")) {
      calibration.colour_correction.blue_brightness = parseFloat(urlParams.get("calibration_override_blue_brightness")!);
    }
    if (urlParams.has("calibration_override_green_brightness")) {
      calibration.colour_correction.green_brightness = parseFloat(urlParams.get("calibration_override_green_brightness")!);
    }

    return calibration;
  }

  // Some stupid devices (iOS cough cough) don't have width and height
  // correctly defined when they are in landscape mode.
  // So always use the largest as width and the smallest as height.
  // We enforce portrait mode, so this should be fine.
  static screen_width(): number {
    return Math.max(screen.width, screen.height);
  }

  // TODO: Take into account devices that cannot go fullscreen.
  static screen_height(): number {
    return Math.min(screen.width, screen.height);
  }

  /**
   * FIT scale that maps a fixed canvas into a fullscreen rectangle:
   * `min(screenWidth / canvasWidth, screenHeight / canvasHeight)`.
   */
  static canvasFitScale(
    screen_width_px: number,
    screen_height_px: number,
    canvas_width_px: number,
    canvas_height_px: number,
  ): number {
    return Math.min(screen_width_px / canvas_width_px, screen_height_px / canvas_height_px);
  }

  /**
   * Set the Phaser world canvas size. FIT is computed against the calibrated
   * screen CSS pixels, not the live DOM parent, so conversion does not depend
   * on fullscreen layout timing.
   */
  setCanvasSize(width_px: number, height_px: number) {
    if (!Number.isFinite(width_px) || !Number.isFinite(height_px) || width_px <= 0 || height_px <= 0) {
      return;
    }
    const scale = ExerciseScreen.canvasFitScale(
      this.calibration.width_px,
      this.calibration.height_px,
      width_px,
      height_px,
    );
    if (!Number.isFinite(scale) || scale <= 0) {
      return;
    }
    this.display_scale = scale;
  }

  get width(): number {
    return this.calibration.width_px;
  }

  get height(): number {
    return this.calibration.height_px;
  }

  /**
   * Calibrated CSS pixels per physical centimeter on the display.
   *
   * Deliberately not zoom-corrected. This is only ever consumed via
   * `pixelsFromCentimeters()`/`centimetersFromPixels()`, which also divide by
   * `display_scale` — the FIT scale computed from these same calibrated screen
   * dimensions against the fixed Phaser canvas resolution. On the
   * height-limited FIT axis the two `calibration.height_px` terms cancel
   * outright; on the width-limited axis what survives is
   * `height_px / width_px`, the screen's aspect ratio, which is itself
   * unaffected by zoom. Either way the conversion reduces to
   * `calibration.height_cm` and the canvas's fixed world-pixel size.
   *
   * Zoom drift is instead absorbed continuously by Phaser's live `Scale.FIT`
   * into a `100vw`/`100vh`/Fullscreen container, which re-fits the canvas to
   * whatever the viewport currently measures. Scaling this value by the zoom
   * factor as well would therefore apply the same correction twice, sizing
   * every exercise target wrong by that factor.
   */
  private cssPixelsPerCentimeter(): number {
    return this.calibration.height_px / this.calibration.height_cm;
  }

  /**
   * Given an offset string, parse it, returning both the value and the unit
   */
  static parseOffset(offset_string: string | number, default_unit: Unit | null = null): Offset {
    if (typeof offset_string == "number") {
      return {
        value: offset_string,
        unit: default_unit || Unit.Pixel,
      };
    }

    offset_string = offset_string.trim();

    let rx = /(-?\d*\.?\d*)\s*(.*)/;
    let match = rx.exec(offset_string);
    if (!match || !match[0] || !match[1]) {
      throw "Unable to parse " + offset_string;
    }
    let value = +match[1];
    let unit;

    // Trim any trailing 's' off the unit
    var raw_unit = match[2].replace(/s$/, "");

    // parse unit
    if (!raw_unit) {
      raw_unit = default_unit || Unit.Pixel;
    }
    if (["px", "pixel", "píxel", "פיקסל", "بكسل", "بيكسل"].includes(raw_unit)) {
      unit = Unit.Pixel;
    } else if (
      ["cm", "centimeter", "centímetro", "centimetro", "centimètre", "centímetro", "centimetre", "סנטימטר", 'ס"מ', "سنتيمتر", "سم"].includes(raw_unit)
    ) {
      unit = Unit.Centimeter;
    } else if (["mm", "millimeter", "millimètre", "milímetro", "milimetro", "מילימטר", 'מ"מ', "مليمتر", "مم"].includes(raw_unit)) {
      unit = Unit.Centimeter;
      value = value / 10;
    } else if (
      [
        "Δ",
        "prism diopter",
        "prism",
        "prisme",
        "prism-diopter",
        "dioptría de prisma",
        "dioptria de prisma",
        "dioptría prismática",
        "dioptria prismática",
        "dioptria prismatica",
        "dioptria de prisma",
        "דיופטרת פריזמטית",
        "דיופטריה",
        "פריזמה",
        "ديوبتر منشوري",
        "منشور",
        "ديوبتر",
        "درجة منشورية",
        "dioptria",
        "dioptría",
        "prisma",
        "prisme",
        "pdpt",
        "prdpt",
        "PD",
        "pd",
      ].includes(raw_unit)
    ) {
      unit = Unit.PrismDiopter;
    } else if (
      [
        "arcsecond",
        "seconde d'arc",
        "arc sec",
        "segundo de arco",
        "arcosegundo",
        "arc second",
        "arcsec",
        "arcseg",
        "asec",
        "as",
        "″",
        '"',
        "''",
        "second",
        "seconde",
        "segundo",
        "segunda",
        "שניית קשת",
        "seg de arco",
        "ثانية قوسية",
        "ثانية",
        "ث",
        "שנ׳", // Hebrew abbreviation for שנייה, used for arcseconds
        "ثا", // Arabic shorthand in some scientific texts for arcseconds
      ].includes(raw_unit)
    ) {
      unit = Unit.Arcsecond;
    } else if (
      [
        "arcminute",
        "arcmin",
        "amin",
        "am",
        "′",
        "'",
        "minute d'arc",
        "minuto de arco",
        "דקת קשת",
        "دقيقة قوسية",
        "دقيقة",
        "د",
        "ד׳", // Hebrew abbreviation for דקה used for arcminutes
        "دق", // Arabic short form for دقيقة قوسية (seen in educational texts)
      ].includes(raw_unit)
    ) {
      unit = Unit.Arcsecond;
      value = value * 60;
    } else if (
      [
        "degree",
        "degrees",
        "deg",
        "degs",
        "°",
        "degré",
        "degré d'arc",
        "מעלה",
        "grado",
        "grados",
        "grau",
        "graus",
        "grau de arco",
        "grado de arco",
        "درجة",
        "°ق",
        "درجات",
        "درجه",
        "מ׳", // Hebrew shorthand for מעלה, sometimes used
        "°م", // Arabic shorthand variant for "degree" (not common but used)
      ].includes(raw_unit)
    ) {
      unit = Unit.Arcsecond;
      value = value * 3600;
    } else {
      throw "Unable to parse " + offset_string;
    }

    return {
      value: value,
      unit: unit,
    };
  }

  /**
   * Check whether the stored calibration still matches the current device,
   * tolerating browser zoom drift (see `calibrationStillMatchesDevice()`)
   * rather than requiring an exact match of screen dimensions/DPR.
   */
  checkCalibration() {
    if (!calibrationStillMatchesDevice(this.calibration, currentCalibrationDeviceIdentity())) {
      throw "Your screen calibration is out of date or you changed devices.";
    }
  }

  /**
   * Get the distance from the screen in cm
   */
  distance_cm(): number {
    return this.calibration.distance_cm;
  }

  /**
   * Convert a physical length in centimeters to Phaser world pixels.
   *
   * The return value is in world pixels, not CSS pixels. Divide by the FIT
   * scale of the canvas into the calibrated screen so that after Phaser
   * paints the canvas fullscreen, the on-screen length matches the physical size.
   */
  pixelsFromCentimeters(cm: number): number {
    return cm * this.cssPixelsPerCentimeter() / this.display_scale;
  }

  /**
   * Pixels per arcsecond
   */
  pixelsFromArcseconds(asec: number): number {
    let cm = this.centimetersFromArcseconds(asec);
    return this.pixelsFromCentimeters(cm);
  }

  /**
   * Pixels per degrees
   */
  pixelsFromDegrees(deg: number): number {
    let cm = this.centimetersFromArcseconds(deg * 3600);
    return this.pixelsFromCentimeters(cm);
  }

  /**
   * Convert a Phaser world-pixel length to physical centimeters.
   *
   * `px` is a world-pixel distance. Multiply by the FIT scale of the canvas
   * into the calibrated screen to recover CSS pixels, then convert with the
   * calibrated screen height.
   */
  centimetersFromPixels(px: number): number {
    return px * this.display_scale / this.cssPixelsPerCentimeter();
  }

  /**
   * Centimeters per arcsecond
   */
  centimetersFromArcseconds(arcs: number): number {
    if (arcs > 648000) {
      throw "Cannot calculate centimeters per arcseconds for angles over 180 degrees.";
    }

    // Focal distance in centimeters
    var f = this.distance_cm();

    // Radians per arcsecond (206265 arcseconds in a radian)
    var rads = arcs / 206265;

    // given apex angle, calculate opposite edge length for an isosceles triangle
    var alpha = (Math.PI - rads) / 2; // Calculate base angles
    var side = f / Math.sin(alpha); // Calculate length of sides
    var base = 2 * side * Math.cos(alpha); // Calculate opposite edge / base (in meters)

    // Return as cm
    return base;
  }

  /**
   * Centimeters from prism diopters
   */
  centimetersFromPrismDiopters(prisms: number): number {
    let asec = this.arcsecondsFromPrismDiopters(prisms);
    return this.centimetersFromArcseconds(asec);
  }

  /**
   * Arcseconds per centimeter
   */
  arcsecondsFromCentimeters(cm: number): number {
    // Focal distance in meters
    var f = this.distance_cm();

    // Formula: θ = atan(2h/a) (this gets the base angle)
    var θ = Math.atan((2 * f) / cm);

    // A triangle has PI radian angles (180 degrees)
    var apex = Math.PI - 2 * θ;

    // Convert to arcseconds (206265 arcseconds in a radian)
    var arcs = apex * 206265;

    return arcs;
  }

  /**
   * Arcseconds from a number of pixels on the screen
   */
  arcsecondsFromPixels(px: number): number {
    let cm = this.centimetersFromPixels(px);
    return this.arcsecondsFromCentimeters(cm);
  }

  /**
   * Degrees of arc from pixels on the screen
   */
  degreesFromPixels(px: number): number {
    let asec = this.arcsecondsFromPixels(px);
    return asec / 3600;
  }

  /**
   * Get the number of arcseconds in the given number of prism dioperes
   *
   * See: https://www.wolframalpha.com/input/?i=1+prism+diopter+in+arcsecond
   */
  arcsecondsFromPrismDiopters(pdpt: number): number {
    // Simplified from:
    // var rads = 2 * Math.atan(0.5 * (0.01 / 1))  // ==> 0.01
    // var degs = rads * 180 / Math.PI             // ==> 0.572953020554149;
    // var arcs = degs * 3600;                     // ==> 2062.630872
    var arcs = 2062.630872;
    return pdpt * arcs;
  }

  /**
   * Get the number of prism dioperes per arcsecond
   */
  prismDioptersFromCentimeters(cm: number): number {
    var asec = this.arcsecondsFromCentimeters(cm);
    return this.prismDioptersFromArcseconds(asec);
  }

  /**
   * Get the number of prism dioperes per arcsecond
   */
  prismDioptersFromArcseconds(arcs: number): number {
    var pdpt = 1 / 2062.630872;
    return pdpt * arcs;
  }

  /**
   * Get total pixel offset from prism diopters
   *
   * If you are offsetting left and right from a centerpoint, divide result by two
   */
  pixelsFromPrismDiopters(pdpt: number): number {
    var arcs = this.arcsecondsFromPrismDiopters(pdpt);
    return this.pixelsFromArcseconds(arcs);
  }

  /**
   * Given an offset string, parse it, returning number of pixels
   */
  offsetToPixels(offset_string: string, default_unit: Unit | null = null): number {
    var offset = ExerciseScreen.parseOffset(offset_string, default_unit);
    return this.pixelsFromOffset(offset);
  }

  /**
   * Given an offset numeric value, parse it given it's unit, returning number of pixels
   */
  pixelsFromOffset(offset: Offset): number {
    switch (offset.unit) {
      case Unit.Pixel:
        return offset.value;
      case Unit.Arcsecond:
        return this.pixelsFromArcseconds(offset.value);
      case Unit.PrismDiopter:
        return this.pixelsFromPrismDiopters(offset.value);
      case Unit.Centimeter:
        return this.pixelsFromCentimeters(offset.value);
      default:
        throw "Unknown unit: " + offset.unit;
    }
  }

  /**
   * Given an offset string, parse it, returning number of cm
   */
  parseOffsetToCentimeters(offset_string: string): number {
    var offset = ExerciseScreen.parseOffset(offset_string);

    switch (offset.unit) {
      case Unit.Pixel:
        return this.centimetersFromPixels(offset.value);
      case Unit.Arcsecond:
        return this.centimetersFromArcseconds(offset.value);
      case Unit.PrismDiopter:
        return this.centimetersFromPrismDiopters(offset.value);
      case Unit.Centimeter:
        return offset.value;
      default:
        throw "Unknown unit: " + offset.unit;
    }
  }

  /**
   * Given an offset string, parse it, returning number of prisms
   */
  parseOffsetToPrisms(offset_string: string): number {
    let offset = ExerciseScreen.parseOffset(offset_string);

    switch (offset.unit) {
      case Unit.Pixel:
        const centimeters = this.centimetersFromPixels(offset.value);
        const arcseconds = this.arcsecondsFromCentimeters(centimeters);
        return this.prismDioptersFromArcseconds(arcseconds);
      case Unit.Arcsecond:
        return this.prismDioptersFromArcseconds(offset.value);
      case Unit.PrismDiopter:
        return offset.value;
      case Unit.Centimeter:
        const arcseconds_2 = this.arcsecondsFromCentimeters(offset.value);
        return this.prismDioptersFromArcseconds(arcseconds_2);
      default:
        throw "Unknown unit: " + offset.unit;
    }
  }

  /**
   * Given an offset string, parse it, returning number of arcseconds
   */
  parseOffsetToArcseconds(offset_string: string): number {
    let offset = ExerciseScreen.parseOffset(offset_string);

    switch (offset.unit) {
      case Unit.Pixel:
        const centimeters = this.centimetersFromPixels(offset.value);
        return this.arcsecondsFromCentimeters(centimeters);
      case Unit.Arcsecond:
        return offset.value;
      case Unit.PrismDiopter:
        return this.arcsecondsFromPrismDiopters(offset.value);
      case Unit.Centimeter:
        return this.arcsecondsFromCentimeters(offset.value);
      default:
        throw "Unknown unit: " + offset.unit;
    }
  }

  /**
   * The the anaglyph colour for a single eye.  Side is specified as 'od' or 'os'.
   */
  anaglyphColour(side: EyeSide, return_as_int = false, cancellation_level = 1): string | number {
    if (
      (side == "od" && this.calibration.anaglyph_orientation == "cyan-red") ||
      (side == "os" && this.calibration.anaglyph_orientation == "red-cyan") ||
      (side == "od" && this.calibration.anaglyph_orientation == "green-red") ||
      (side == "os" && this.calibration.anaglyph_orientation == "red-green")
    ) {
      let red_bright = this.calibration.colour_correction.red_brightness || 1;
      if (cancellation_level != 1) {
        red_bright = red_bright + (1 - red_bright) * (1 - cancellation_level);
      }
      return parseColour("#FF0000", return_as_int, red_bright);
    } else if ((side == "od" && this.calibration.anaglyph_orientation == "red-cyan") || (side == "os" && this.calibration.anaglyph_orientation == "cyan-red")) {
      let blue_bright = this.calibration.colour_correction.blue_brightness || 1;
      if (cancellation_level != 1) {
        blue_bright = blue_bright + (1 - blue_bright) * (1 - cancellation_level);
      }
      return parseColour("#0000FF", return_as_int, blue_bright);
    } else if (
      (side == "od" && this.calibration.anaglyph_orientation == "red-green") ||
      (side == "os" && this.calibration.anaglyph_orientation == "green-red")
    ) {
      let green_bright = this.calibration.colour_correction.green_brightness || 1;
      if (cancellation_level != 1) {
        green_bright = green_bright + (1 - green_bright) * (1 - cancellation_level);
      }
      return parseColour("#00FF00", return_as_int, green_bright);
    } else {
      throw "singleAnaglyphColour: invalid side: " + side + " (orientation: " + this.calibration.anaglyph_orientation + ")";
    }
  }

  /**
   * Get an array of anaglyph colours, suitable to choosing a random colour
   */
  anaglyphColoursArray(return_as_int = false, include_combined = false, cancellation_level = 1, white_background = false): number[] | string[] {
    let red_bright = this.calibration.colour_correction.red_brightness || 1;
    let blue_bright = this.calibration.colour_correction.blue_brightness || 1;
    let green_bright = this.calibration.colour_correction.green_brightness || 1;

    if (cancellation_level != 1) {
      red_bright = red_bright + (1 - red_bright) * (1 - cancellation_level);
      blue_bright = blue_bright + (1 - blue_bright) * (1 - cancellation_level);
      green_bright = green_bright + (1 - green_bright) * (1 - cancellation_level);
    }

    let red, blue, green;
    if (white_background) {
      red = parseColour("#FF0000", return_as_int, 1, 1 - red_bright);
      blue = parseColour("#0000FF", return_as_int, 1, 1 - blue_bright);
      green = parseColour("#00FF00", return_as_int, 1, 1 - green_bright);
    } else {
      red = parseColour("#FF0000", return_as_int, red_bright);
      blue = parseColour("#0000FF", return_as_int, blue_bright);
      green = parseColour("#00FF00", return_as_int, green_bright);
    }

    let colours = [red];
    if (this.calibration.anaglyph_orientation == "red-cyan" || this.calibration.anaglyph_orientation == "cyan-red") {
      colours.push(blue);
    }
    if (this.calibration.anaglyph_orientation == "red-green" || this.calibration.anaglyph_orientation == "green-red") {
      colours.push(green);
    }
    if (include_combined) {
      colours.push(this.combinedAnalyphColour(return_as_int, cancellation_level));
    }

    return colours as number[] | string[];
  }

  /**
   * Get the colour that is the combination of the two configured anaglyph colours.
   * red-cyan => magenta
   * red-green => yellow
   */
  combinedAnalyphColour(return_as_int = false, cancellation_level = 1, white_background = false): string | number {
    let red_bright = this.calibration.colour_correction.red_brightness || 1;
    let blue_bright = this.calibration.colour_correction.blue_brightness || 1;
    let green_bright = this.calibration.colour_correction.green_brightness || 1;

    if (cancellation_level != 1) {
      red_bright = red_bright + (1 - red_bright) * (1 - cancellation_level);
      blue_bright = blue_bright + (1 - blue_bright) * (1 - cancellation_level);
      green_bright = green_bright + (1 - green_bright) * (1 - cancellation_level);
    }

    let magenta_bright = (red_bright + blue_bright) / 2;
    let yellow_bright = (red_bright + green_bright) / 2;

    var magenta, yellow;
    if (white_background) {
      magenta = parseColour("#FF00FF", return_as_int, 1, 1 - magenta_bright);
      yellow = parseColour("#FFFF00", return_as_int, 1, 1 - yellow_bright);
    } else {
      magenta = parseColour("#FF00FF", return_as_int, magenta_bright);
      yellow = parseColour("#FFFF00", return_as_int, yellow_bright);
    }

    if (this.calibration.anaglyph_orientation == "red-cyan" || this.calibration.anaglyph_orientation == "cyan-red") {
      return magenta;
    }
    if (this.calibration.anaglyph_orientation == "red-green" || this.calibration.anaglyph_orientation == "green-red") {
      return yellow;
    }

    throw "combinedAnalyphColour: invalid orientation: " + this.calibration.anaglyph_orientation;
  }

  /**
  * Return a 4x5 color matrix (20 numbers) suitable for Phaser ColorMatrixFX,
  * for the given eye and anaglyph orientation.
  */
  anaglyphColorMatrix(side: EyeSide, cancellation_level = 1): number[] {
    const { anaglyph_orientation, colour_correction } = this.calibration;

    // Base brightness per channel from calibration
    const redBase = colour_correction.red_brightness ?? 1;
    const greenBase = colour_correction.green_brightness ?? 1;
    const blueBase = colour_correction.blue_brightness ?? 1;

    // You can swap this for your old non-linear formula if you prefer.
    const scale = (base: number) => base * cancellation_level;

    let r = 0;
    let g = 0;
    let b = 0;

    // Which channel is “active” for this eye?
    if (
      (side === "od" && (anaglyph_orientation === "cyan-red" || anaglyph_orientation === "green-red")) ||
      (side === "os" && (anaglyph_orientation === "red-cyan" || anaglyph_orientation === "red-green"))
    ) {
      r = scale(redBase); // red-only eye
    }
    //   blue eye when: (od, red-cyan) or (os, cyan-red)
    else if (
      (side === "od" && anaglyph_orientation === "red-cyan") ||
      (side === "os" && anaglyph_orientation === "cyan-red")
    ) {
      b = scale(blueBase); // blue-only eye
    }
    //   green eye when: (od, red-green) or (os, green-red)
    else if (
      (side === "od" && anaglyph_orientation === "red-green") ||
      (side === "os" && anaglyph_orientation === "green-red")
    ) {
      g = scale(greenBase); // green-only eye
    } else {
      throw new Error(
        `anaglyphColorMatrix: invalid side: ${side} (orientation: ${anaglyph_orientation})`
      );
    }

    // --- Build the color matrix ---
    // Layout:
    // [ 0] Rr  [ 1] Rg  [ 2] Rb  [ 3] Ra  [ 4] Rt
    // [ 5] Gr  [ 6] Gg  [ 7] Gb  [ 8] Ga  [ 9] Gt
    // [10] Br  [11] Bg  [12] Bb  [13] Ba  [14] Bt
    // [15] Ar  [16] Ag  [17] Ab  [18] Aa  [19] At
    //
    // We only want the source R,G,B to contribute to the *same* channel, so we
    // leave the diagonal and zero all cross-terms. Non-target channels stay at 0.
    return [
      r, 0, 0, 0, 0,   // R' = r * R
      0, g, 0, 0, 0,   // G' = g * G
      0, 0, b, 0, 0,   // B' = b * B
      0, 0, 0, 1, 0    // A' = A
    ];
  }
}

